import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import ReactTable from "react-table";
import moment from "moment";
import {Card, CardContent} from "@material-ui/core";
import {Link} from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import ReportDateToolbar, {
	buildReportApiFilter,
	createDefaultReportDateState,
	defaultReportFilterState,
	formatDateRangeLabel,
	formatReportMoney,
} from "components/Elements/ReportFilterToggles";
import {
	formatBalanceRemaining,
	formatReceivablesDelta,
	isInvoicingEnabledForReport,
	ReportReceivablesHeaderFields,
	sumReceivablesDeltaFromData,
} from "components/Elements/ReportReceivablesExtras";

const DATE_STORAGE_KEY = "dateSalesTax";

export default class SalesReport extends Framework.Components.Collection {
	constructor(props) {
		let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ_ALL);
		super(props, model);
		//this.limit = 1;
		//this.state.limit = 1;
		this.request = "GET_SALES_REPORT";

		Object.assign(this.state, defaultReportFilterState, createDefaultReportDateState(DATE_STORAGE_KEY));
	}

	handleLayawayModeChange = async (completedLayawaysOnly) => {
		await this.setState({completedLayawaysOnly});
		await this.getReport();
	};

	handleWorkOrderModeChange = async (completedWorkOrdersOnly) => {
		await this.setState({completedWorkOrdersOnly});
		await this.getReport();
	};

	handleTimezoneModeChange = async (useLocalTimezone) => {
		await this.setState({useLocalTimezone});
		await this.getReport();
	};

	async componentDidMount() {
		// Added to correct values in the report if user refreshes the page.
		window.addEventListener('beforeunload', this.handleUnload);
		await this.getReport();
	}

	handleToggleModal = (modal) => {
		this.setState({[modal]: !this.state[modal]})
	}

	handleDateChange = async (name, date) => {
		await this.setState({filterDate: moment(date).format()});
		this.handleToggleModal("showDate");
		await this.getReport();
	};

	handleCancelDateRange = () => {
		const today = moment().format("YYYY-MM-DD");
		this.setState({start_date: today, end_date: today});
		this.handleToggleModal("showDateRange");
	};

	getReport = async () => {
		await this.setState({totals: null, loading: true});
		try {
			let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ);
			this.read = this.request;
			model.filter = buildReportApiFilter(this.state);
			this.state.filter = model.filter;
			const dateRangeFormatted = formatDateRangeLabel(
				this.state.start_date,
				this.state.end_date
			);
			model.search = this.state.search;
			const response = await model.submit(null, this.read);
			this.data = response.items;
			//TODO whenever we allow for more than 1 location, this limit will need to change
			await this.setState({
				data: this.data,
				totals: response.totals,
				dateRangeFormatted,
				key: Math.random(),
			});
			localStorage.setItem(DATE_STORAGE_KEY, this.state.filterDate);
		} finally {
			await this.setState({loading: false});
		}
	}

	getDiscountTotal = (props) => {
		const discounts =
			parseFloat(props.item_discounts ?? 0) +
			parseFloat(props.product_discounts ?? 0) +
			parseFloat(props.service_discounts ?? 0);
		const discountPct = parseFloat(props.discount_percentage ?? 0);
		let discountPercentTotal = 0;
		if (discountPct > 0) {
			const netBeforeTax =
				parseFloat(props.net_sale ?? 0) - parseFloat(props.tax_amount ?? 0);
			const trade = parseFloat(props.trade ?? 0);
			const divisor = 1 - discountPct / 100;
			if (divisor > 0) {
				const preDiscountSubtotal = (netBeforeTax + trade) / divisor;
				discountPercentTotal = preDiscountSubtotal * (discountPct / 100);
			}
		}
		const total =
			discounts + discountPercentTotal + parseFloat(props.discount_amount ?? 0);
		return Number.isFinite(total) ? total.toFixed(2) : "0.00";
	}

	getNetSale = (r) => {
		return parseFloat(r.net_sale) - parseFloat(r.tax_amount);
	}

	getCost = (r) => {
		return (
			parseFloat(r.product_cost ?? 0.00) +
			parseFloat(r.item_cost ?? 0.00) +
			parseFloat(r.service_cost ?? 0.00)
		).toFixed(2);
	}

	fetchData = async (state) => {
		this.setState({loading: true});
		window.setTimeout(() => {
			this.fetchDataActual(state, ['totals']);
		}, 100);
	}

	// When user leaves the page, it will remove the table-preferences from local storage so values in the report are
	// correct in regard to the initial date range.
	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.handleUnload);
		localStorage.removeItem('table-preferences-SalesReport');
	}

	// This function corrects the values in the report if the page is refreshed.
	handleUnload = async () => {
		localStorage.removeItem('table-preferences-SalesReport');
	}

	render() {
		const {data, pages, loading, limit} = this.state;
		const totalsRow = this.state.totals ? this.state.totals[0] : null;
		const invoicingEnabled = isInvoicingEnabledForReport(totalsRow, this.props);
		const netReceivablesChange = totalsRow?.net_receivables_change != null
			? totalsRow.net_receivables_change
			: sumReceivablesDeltaFromData(data);
		let columns = [
			{
				Header: "Date Time",
				id: "date",
				width: 180,
				accessor: d => {
					return moment(d.created).local().format("MM/DD/YYYY hh:mm A")
				},
				sortable: false
			},
			{
				Header: "Cost",
				className: 'right-column',
				id: "cost",
				accessor: r => this.getCost(r),
				sortable: false
			},
			{
				Header: "Gross Sale",
				className: 'right-column',
				id: "gross_sale",
				accessor: r => (parseFloat(this.getNetSale(r)) + parseFloat(this.getDiscountTotal(r))).toFixed(2),
				sortable: false
			},
			{
				Header: "Discounts",
				className: 'right-column',
				id: "discounts",
				accessor: r => this.getDiscountTotal(r),
				sortable: false
			},
			{
				Header: "Returns",
				className: 'right-column',
				id: "refund_amount",
				accessor: r => r.refund_amount ?? 0.00,
				sortable: false
			},
			{
				Header: "NET Sale",
				className: 'right-column',
				id: "net_sale",
				accessor: r => parseFloat(this.getNetSale(r)).toFixed(2),
				sortable: false
			},
			{
				Header: "Tax",
				className: 'right-column',
				id: "tax_amount",
				accessor: r => r.tax_amount,
				sortable: false
			},
			...(invoicingEnabled ? [
				{
					Header: "Receivables Change",
					id: "receivables_delta",
				className: 'right-column',
					accessor: r => formatReceivablesDelta(r),
					Footer: () => (
						<span title="Net Receivables Change">
							<strong>{formatReportMoney(netReceivablesChange)}</strong>
						</span>
					),
					sortable: false
				},
				{
					Header: "Remaining Balance",
					id: "balance_remaining",
					accessor: r => formatBalanceRemaining(r),
					sortable: false
				},
			] : []),
			{
				Header: "NET Profit",
				className: 'right-column',
				id: "net_profit",
				accessor: r => (parseFloat(this.getNetSale(r)) - parseFloat(this.getCost(r)) - (parseFloat(r.refund_amount ?? 0.00))).toFixed(2),
				sortable: false
			},
			{
				Header: "Method",
				id: "payment_method",
				className: 'center-column',
				accessor: r => r.payment_method,
				sortable: false
			},
			{
				Header: "Customer",
				id: "client",
				accessor: r => r.client,
				sortable: false
			},
			{
				Header: "Order #",
				className: 'center-column',
				id: "pkey",
				sortable: false,
				accessor: (r) => {
					if ((r.source_type || '') === 'layaway_payment' && r.layaway_pkey != null && r.layaway_pkey !== '') {
						return r.layaway_pkey;
					}
					if ((r.source_type || '') === 'work_order_payment' && r.work_order_pkey != null && r.work_order_pkey !== '') {
						return r.work_order_pkey;
					}
					if ((r.source_type || '') === 'invoice_payment' && r.source_pkey != null && r.source_pkey !== '') {
						return r.source_pkey;
					}
					if ((r.source_type || '') === 'invoice_write_off' && r.source_pkey != null && r.source_pkey !== '') {
						return r.source_pkey;
					}
					if ((r.source_type || '') === 'layaway_originated' && r.layaway_pkey != null && r.layaway_pkey !== '') {
						return r.layaway_pkey;
					}
					if ((r.source_type || '') === 'layaway_payment_refund' && r.layaway_pkey != null && r.layaway_pkey !== '') {
						return r.layaway_pkey;
					}
					return r.pkey;
				},
				Cell: (props) => {
					const row = props.original;
					const layawayRow = ((row.source_type || '') === 'layaway_payment' || (row.source_type || '') === 'layaway_originated' || (row.source_type || '') === 'layaway_payment_refund')
						&& row.layaway_pkey != null && row.layaway_pkey !== '';
					const workOrderRow = (row.source_type || '') === 'work_order_payment' && row.work_order_pkey != null && row.work_order_pkey !== '';
					const invoiceRow = ((row.source_type || '') === 'invoice_payment' || (row.source_type || '') === 'invoice_write_off') && row.source_pkey != null && row.source_pkey !== '';
					const id = layawayRow ? row.layaway_pkey : (workOrderRow ? row.work_order_pkey : (invoiceRow ? row.source_pkey : row.pkey));
					const href = layawayRow ? `/layaways/${id}` : (workOrderRow ? `/work-orders/${id}` : (invoiceRow ? `/invoices/${id}` : `/transactions/${id}`));
					return (
						<span>
							<Link to={href}>{id}</Link>
						</span>
					);
				}
			},
			{
				Header: "Items",
				id: "items",
				accessor: r => [r.item_names, r.product_names, r.service_names].filter(n => n != null && n !== '').join(', '),
				sortable: false
			},
			{
				Header: "Seller",
				id: "seller",
				accessor: r => r.seller !== "" ? r.seller : r.account_name,
				sortable: false
			},
			{
				Header: "Salesman",
				id: "salesman",
				accessor: r => r.salesman,
				sortable: false
			},
		];

		return (
			<Card>
				<CardContent>
					<div className="table-bar">
						<ReportDateToolbar
							filterDate={this.state.filterDate}
							start_date={this.state.start_date}
							end_date={this.state.end_date}
							dateRangeFormatted={this.state.dateRangeFormatted}
							showDate={this.state.showDate}
							showStartDate={this.state.showStartDate}
							showEndDate={this.state.showEndDate}
							showDateRange={this.state.showDateRange}
							onToggleModal={this.handleToggleModal}
							onSingleDateChange={this.handleDateChange}
							onStartDateChange={(start_date) => this.setState({start_date})}
							onEndDateChange={(end_date) => this.setState({end_date})}
							onApplyDateRange={this.getReport}
							onCancelDateRange={this.handleCancelDateRange}
							toggleClose={this.toggleClose}
							completedLayawaysOnly={this.state.completedLayawaysOnly}
							completedWorkOrdersOnly={this.state.completedWorkOrdersOnly}
							useLocalTimezone={this.state.useLocalTimezone}
							onLayawayModeChange={this.handleLayawayModeChange}
							onWorkOrderModeChange={this.handleWorkOrderModeChange}
							onTimezoneModeChange={this.handleTimezoneModeChange}
						/>
						<div className="d-flex" style={{paddingTop: 15}}>
							<TextField
								placeholder={"0.00"}
								label={'Total Card Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_card) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Cash Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_cash) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Terminal Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_terminal) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Gift Cert Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_gift) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Check Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_check) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<TextField
								placeholder={"0.00"}
								label={'Total Zelle Amount'}
								margin={"dense"}
								variant="outlined"
								value={this.state.totals ? formatReportMoney(this.state.totals[0].total_zelle) : "0.00"}
								disabled={true}
								InputLabelProps={{shrink: true}}
							/>
							<ReportReceivablesHeaderFields
								invoicingEnabled={invoicingEnabled}
								totalsRow={totalsRow}
								variant="sales"
							/>
							{this.renderHeaderRight("Sales Report",columns)}
						</div>
					</div>
					{!loading && (data === 'undefined' || data[0]?.pkey === null) ?
						<div>
							There is no data for the dates selected.
						</div>
						:
					<ReactTable
						key={this.state.key}
						columns={columns}
						manual // Forces table not to paginate or sort automatically, so we can handle it server-side
						data={data}
						pages={pages}
						loading={loading} // Display the loading overlay when we need it
						loadingText="Requesting Data"
						onFetchData={this.fetchData} // Request new data when things change
						defaultSorted={[
							{
								id: this.model.sort,
								desc: this.model.order === 'ascending'
							}
						]}
						className="-striped -highlight"
						showPaginationTop={false}
						showPaginationBottom={false}
						showPageSizeOptions={true}
						defaultPageSize={limit}
						getTheadGroupProps={() => ({ style: { borderBottom: '1px solid #e0e0e0' } })}
						getTheadThProps={() => ({ style: { fontWeight: 600 } })}
						getTfootProps={() => ({ style: { fontWeight: 600 } })}
					/>
					}
					<Framework.Elements.LoadingSpinner isLoading={loading} />
				</CardContent>
			</Card>
		)
	}
}
